import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import { payrollConstants } from "./types";
import payrollService from "services/payrollService";
import ActionCreator from "utils/actionHandler";

export const getPayrollLineItems = (
  client: ApolloClient<object>,
  groupId: string,
  payPeriodId?: string | null
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.getPayrollLineItems>>
    >(payrollConstants.GET_PAYROLL_LINE_ITEMS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.getPayrollLineItems(client, groupId, payPeriodId),
      { id: groupId }
    );
  };
};

export const approvePayroll = (
  client: ApolloClient<object>,
  payload: Parameters<typeof payrollService.approvePayroll>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.approvePayroll>>
    >(payrollConstants.APPROVE_PAYROLL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.approvePayroll(client, payload),
      { id: payload.groupId }
    );
  };
};

export const getPayrollHistory = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.getPayrollHistoryRequest>>
    >(payrollConstants.GET_PAYROLL_HISTORY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.getPayrollHistoryRequest(client)
    );
  };
};

export const cancelPayrollsByBankId = (
  client: ApolloClient<object>,
  bankId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.cancelPayrollsByBankIdRequest>>
    >(payrollConstants.CANCEL_PAYROLLS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.cancelPayrollsByBankIdRequest(client, bankId)
    );
  };
};

export const getTotalAmountSaved = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.getTotalAmountSavedRequest>>
    >(payrollConstants.GET_TOTAL_AMOUNT_SAVED);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.getTotalAmountSavedRequest(client)
    );
  };
};

export const createTopOffContributions = (
  client: ApolloClient<object>,
  groupId: string,
  payPeriodId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof payrollService.createTopOffContributionsRequest>
      >
    >(payrollConstants.CREATE_TOP_OFF_CONTRIBUTIONS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.createTopOffContributionsRequest(
        client,
        groupId,
        payPeriodId
      )
    );
  };
};
export const getPaginatedChangeRequests = (
  client: ApolloClient<object>,
  params: Parameters<typeof payrollService.getPaginatedChangeRequests>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.getPaginatedChangeRequests>>
    >(payrollConstants.GET_PAGINATED_CHANGE_REQUESTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.getPaginatedChangeRequests(client, params)
    );
  };
};

export const getPaginatedContributions = (
  client: ApolloClient<object>,
  params: Parameters<typeof payrollService.getPaginatedContributions>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.getPaginatedContributions>>
    >(payrollConstants.GET_PAGINATED_CONTRIBUTIONS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.getPaginatedContributions(client, params)
    );
  };
};

export const cancelPayroll = (
  client: ApolloClient<object>,
  groupId: string,
  payrollId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<{
      groupId: string;
      schedules: Awaited<
        ReturnType<typeof payrollService.cancelPayrollRequest>
      >;
    }>(payrollConstants.CANCEL_PAYROLL);
    dispatch(actionCreator.request());
    return payrollService
      .cancelPayrollRequest(client, groupId, payrollId)
      .then((schedules) =>
        dispatch(actionCreator.success({ groupId, schedules }))
      )
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

export const recheckPayrollBalance = (
  client: ApolloClient<object>,
  payrollId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.recheckPayrollBalanceRequest>>
    >(payrollConstants.RECHECK_PAYROLL_BALANCE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.recheckPayrollBalanceRequest(client, payrollId)
    );
  };
};

export const dismissPayroll = (
  client: ApolloClient<object>,
  payPeriodId: string,
  reason: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.dismissPayroll>>
    >(payrollConstants.DISMISS_PAYROLL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.dismissPayroll(client, payPeriodId, reason)
    );
  };
};

export const approveSessionTokenPayroll = (
  client: ApolloClient<object>,
  params: Parameters<typeof payrollService.approveSessionTokenPayroll>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof payrollService.approveSessionTokenPayroll>>
    >(payrollConstants.APPROVE_SESSION_TOKEN_PAYROLL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      payrollService.approveSessionTokenPayroll(client, params)
    );
  };
};
